import React, { useState, useRef, useEffect } from 'react';
import { ImageConverterService, ImageFormat } from '../../lib/services/imageConverterService';
import { ErrorHandler } from '../../lib/utils/errorHandler';
import { EncoderToolType } from '../../lib/types/EncodingOperation';
import './ImageConverter.css';

export const ImageConverter: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [originalImageUrl, setOriginalImageUrl] = useState<string>('');
  const [convertedImageUrl, setConvertedImageUrl] = useState<string>('');
  const [targetFormat, setTargetFormat] = useState<ImageFormat>('image/png');
  const [quality, setQuality] = useState<number>(90);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversionMetadata, setConversionMetadata] = useState<any>(null);
  const [downloadFeedback, setDownloadFeedback] = useState<'idle' | 'downloading' | 'error'>('idle');
  const [dragActive, setDragActive] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const processTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Process image with debouncing
  useEffect(() => {
    if (selectedFile) {
      if (processTimeoutRef.current) {
        clearTimeout(processTimeoutRef.current);
      }

      processTimeoutRef.current = setTimeout(() => {
        processImage();
      }, 300);
    }

    return () => {
      if (processTimeoutRef.current) {
        clearTimeout(processTimeoutRef.current);
      }
    };
  }, [selectedFile, targetFormat, quality]);

  // Update quality when format changes
  useEffect(() => {
    const defaultQuality = ImageConverterService.getDefaultQuality(targetFormat);
    setQuality(defaultQuality);
  }, [targetFormat]);

  const processImage = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setError(null);

    try {
      const result = await ImageConverterService.convert(selectedFile, targetFormat, { quality });

      if (result.status === 'error') {
        setError(result.errorMessage || 'Conversion failed');
        setConvertedImageUrl('');
        setConversionMetadata(null);
      } else if (result.outputData) {
        const url = URL.createObjectURL(result.outputData as Blob);
        setConvertedImageUrl(url);
        setConversionMetadata(result.metadata);
      }
    } catch (err) {
      const errorMessage = ErrorHandler.handle(err, EncoderToolType.IMAGE_CONVERTER);
      setError(errorMessage);
      setConvertedImageUrl('');
      setConversionMetadata(null);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileSelect = (file: File) => {
    // Cleanup previous URLs
    if (originalImageUrl) {
      URL.revokeObjectURL(originalImageUrl);
    }
    if (convertedImageUrl) {
      URL.revokeObjectURL(convertedImageUrl);
    }

    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setOriginalImageUrl(url);
    setError(null);
    setConversionMetadata(null);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDownload = () => {
    if (!convertedImageUrl) return;

    setDownloadFeedback('downloading');

    try {
      const link = document.createElement('a');
      link.href = convertedImageUrl;
      const formatInfo = ImageConverterService.getFormatInfo(targetFormat);
      const originalName = selectedFile?.name.split('.')[0] || 'converted';
      link.download = `${originalName}_converted.${formatInfo.extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => {
        setDownloadFeedback('idle');
      }, 2000);
    } catch (err) {
      setDownloadFeedback('error');
      setTimeout(() => {
        setDownloadFeedback('idle');
      }, 2000);
    }
  };

  const handleClearImage = () => {
    if (originalImageUrl) {
      URL.revokeObjectURL(originalImageUrl);
    }
    if (convertedImageUrl) {
      URL.revokeObjectURL(convertedImageUrl);
    }
    setSelectedFile(null);
    setOriginalImageUrl('');
    setConvertedImageUrl('');
    setError(null);
    setConversionMetadata(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const supportedFormats = ImageConverterService.getSupportedFormats();
  const supportsQuality = ImageConverterService.supportsQuality(targetFormat);
  const formatInfo = ImageConverterService.getFormatInfo(targetFormat);

  // Cleanup URLs on unmount
  useEffect(() => {
    return () => {
      if (originalImageUrl) {
        URL.revokeObjectURL(originalImageUrl);
      }
      if (convertedImageUrl) {
        URL.revokeObjectURL(convertedImageUrl);
      }
    };
  }, []);

  return (
    <div className="image-converter">
      <div className="container">
        <header className="header">
          <h1>Image Converter</h1>
          <p className="description">
            Convert images between PNG, JPEG, and WebP formats with quality control
          </p>
        </header>

        {/* File Upload */}
        <div className="upload-section">
          <div
            className={`drop-zone ${dragActive ? 'drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            aria-label="Upload image file"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
          >
            <svg className="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="drop-zone-text">
              <strong>Click to upload</strong> or drag and drop
            </p>
            <p className="drop-zone-hint">PNG, JPEG, WEBP, GIF (max 10MB)</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleFileInputChange}
            className="file-input"
            aria-label="File input"
          />
        </div>

        {selectedFile && (
          <div className="file-info">
            <div className="file-details">
              <strong>Selected file:</strong> {selectedFile.name} ({ImageConverterService.formatFileSize(selectedFile.size)})
            </div>
            <button
              onClick={handleClearImage}
              className="btn-clear"
              aria-label="Clear selected image"
            >
              Clear
            </button>
          </div>
        )}

        {/* Format Selector */}
        <div className="format-selector">
          <label htmlFor="target-format" className="label">
            Target Format:
          </label>
          <select
            id="target-format"
            value={targetFormat}
            onChange={(e) => setTargetFormat(e.target.value as ImageFormat)}
            className="select"
            disabled={!selectedFile || isProcessing}
          >
            {supportedFormats.map((format) => {
              const info = ImageConverterService.getFormatInfo(format);
              return (
                <option key={format} value={format}>
                  {info.name} (.{info.extension})
                  {info.supportsTransparency ? ' - Supports transparency' : ''}
                </option>
              );
            })}
          </select>
        </div>

        {/* Quality Slider */}
        {supportsQuality && selectedFile && (
          <div className="quality-section">
            <label htmlFor="quality-slider" className="label">
              Quality: <span className="quality-value">{quality}%</span>
            </label>
            <input
              id="quality-slider"
              type="range"
              min="1"
              max="100"
              value={quality}
              onChange={(e) => setQuality(parseInt(e.target.value))}
              className="quality-slider"
              disabled={isProcessing}
              aria-label="Quality slider"
            />
            <div className="quality-labels">
              <span>Lower size</span>
              <span>Higher quality</span>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="error-message" role="alert">
            <svg className="error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}

        {/* Conversion Results */}
        {conversionMetadata && (
          <div className="conversion-info">
            <h3>Conversion Details</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Original Size:</span>
                <span className="info-value">{ImageConverterService.formatFileSize(conversionMetadata.sourceSize)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Converted Size:</span>
                <span className="info-value">{ImageConverterService.formatFileSize(conversionMetadata.targetSize)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Size Change:</span>
                <span className="info-value">{conversionMetadata.compressionRatio}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Quality:</span>
                <span className="info-value">{conversionMetadata.quality}%</span>
              </div>
            </div>
          </div>
        )}

        {/* Image Preview */}
        {originalImageUrl && (
          <div className="preview-section">
            <div className="preview-container">
              <div className="preview-wrapper">
                <h3 className="preview-title">Original</h3>
                <img
                  src={originalImageUrl}
                  alt="Original uploaded image"
                  className="preview-image"
                />
              </div>
              <div className="preview-wrapper">
                <h3 className="preview-title">
                  Converted ({formatInfo.name})
                  {isProcessing && <span className="processing-badge">Processing...</span>}
                </h3>
                {convertedImageUrl ? (
                  <img
                    src={convertedImageUrl}
                    alt={`Converted image in ${formatInfo.name} format`}
                    className="preview-image"
                  />
                ) : (
                  <div className="placeholder">
                    {isProcessing ? 'Converting...' : 'No conversion available'}
                  </div>
                )}
              </div>
            </div>

            {/* Download Button */}
            {convertedImageUrl && (
              <button
                onClick={handleDownload}
                className="btn-download"
                disabled={downloadFeedback === 'downloading'}
                aria-label="Download converted image"
              >
                {downloadFeedback === 'downloading' ? 'Downloading...' : 'Download Converted Image'}
              </button>
            )}
          </div>
        )}

        {/* Progressive Enhancement Fallback */}
        <noscript>
          <div className="fallback-form">
            <h3>JavaScript Required</h3>
            <p>This tool requires JavaScript to convert images in real-time.</p>
            <p>For command-line conversion, consider using tools like:</p>
            <pre><code>
              # Using ImageMagick
              convert input.png output.jpg
              convert input.jpg -quality 90 output.webp
            </code></pre>
          </div>
        </noscript>
      </div>
    </div>
  );
};
